import { mountToAppShell } from '../appShell'
import { NetworkManager } from '../../network/NetworkManager'
import { PaymentClient } from '../../net/PaymentClient'
import { NeonColors, FONTS, neonTitleGlow, cosmicBackground, tgStar } from '../neon-theme'
import {
  formatBestDailyRank,
  formatEvacRate,
  formatNumberOrDash,
  formatRegistrationDate,
  formatStarsOrDash,
  formatStreakOrDash,
  pluralDay,
  resolvePlayerName,
  safeInt,
} from './profileFormat'
import { mountSnakePreview } from './profileSnakePreview'

/**
 * Player profile (SNAKE-18 + extended in SNAKE-30 with Wallet/Settings tabs).
 *
 * Three tabs:
 *   - Статистика — header + balances summary + stats grid + records + streak
 *   - Кошелёк   — current balance, top up (Telegram Stars invoice), payout
 *   - Настройки — display name input + snake skin preview + notification placeholder
 *
 * Wallet/Settings moved here from MainMenuScreen as part of the menu UX overhaul:
 * the main screen is now a 6-block layout that does NOT carry these flows.
 */

const NAME_STORAGE_KEY = 'star-snake-player-name'

export interface ProfileSummary {
  userId?: string
  telegramId?: string | null
  username?: string | null
  firstName?: string | null
  createdAt?: string

  balance?: number
  totalStarsEarned?: number
  totalStarsSpent?: number

  gamesPlayed?: number
  evacuations?: number
  kills?: number
  deaths?: number
  longestSnake?: number
  kdRatio?: number
  evacuationRate?: number

  biggestEvacuation?: number
  bestDailyStars?: number
  bestDailyRank?: number | null

  currentDailyStreak?: number
  longestDailyStreak?: number
  currentEvacStreak?: number
  longestEvacStreak?: number
  dailyRewardStreak?: number
}

type ProfileTab = 'stats' | 'wallet' | 'settings'

export class ProfileScreen {
  private container: HTMLDivElement
  private onClose: (() => void) | null = null
  private currentTab: ProfileTab = 'stats'
  private currentStats: ProfileSummary | null = null
  private tabContentEl: HTMLDivElement | null = null
  private tabButtons: Map<ProfileTab, HTMLButtonElement> = new Map()

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'profile-screen'
    this.container.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      background: ${NeonColors.bgOverlay};
      z-index: 210;
      font-family: ${FONTS.body}; color: ${NeonColors.textBright};
      overflow-y: auto;
    `
  }

  show(onClose: () => void, opts: { initialTab?: ProfileTab } = {}): void {
    this.onClose = onClose
    this.currentTab = opts.initialTab ?? 'stats'
    this.renderLoading()
    mountToAppShell(this.container)
    void this.fetchProfile()
  }

  hide(): void {
    this.container.remove()
  }

  dispose(): void {
    this.hide()
    this.onClose = null
    this.currentStats = null
    this.tabContentEl = null
    this.tabButtons.clear()
  }

  private renderLoading(): void {
    this.clearContainer()
    this.appendBackground()
    const wrap = document.createElement('div')
    wrap.style.cssText = `
      position: relative; z-index: 1;
      text-align: center; color: ${NeonColors.textMuted};
      padding: 60px 20px;
    `
    wrap.textContent = 'Загрузка профиля...'
    this.container.appendChild(wrap)
  }

  private renderError(): void {
    this.clearContainer()
    this.appendBackground()
    const wrap = document.createElement('div')
    wrap.style.cssText = `
      position: relative; z-index: 1;
      text-align: center; color: ${NeonColors.danger};
      padding: 60px 20px;
    `
    wrap.textContent = 'Не удалось загрузить профиль'
    const retry = document.createElement('button')
    retry.type = 'button'
    retry.style.cssText = `
      display: block; margin: 18px auto 0;
      padding: 8px 18px; border-radius: 10px;
      border: 1px solid ${NeonColors.textMuted};
      background: transparent; color: ${NeonColors.textMuted};
      cursor: pointer; font-family: ${FONTS.body};
    `
    retry.textContent = 'Повторить'
    retry.addEventListener('click', () => {
      this.renderLoading()
      void this.fetchProfile()
    })
    wrap.appendChild(retry)
    this.container.appendChild(wrap)
  }

  private clearContainer(): void {
    while (this.container.firstChild) this.container.removeChild(this.container.firstChild)
  }

  private appendBackground(): void {
    const bg = document.createElement('div')
    bg.style.cssText = 'position:absolute; inset:0; pointer-events:none;'
    bg.innerHTML = cosmicBackground()
    this.container.appendChild(bg)
  }

  private async fetchProfile(): Promise<void> {
    const headers = this.getHeaders()
    if (!headers) {
      this.renderError()
      return
    }

    try {
      const res = await fetch('/api/profile/stats', { headers })
      if (!res.ok) {
        this.renderError()
        return
      }
      const body = (await res.json()) as { success: boolean; data?: ProfileSummary }
      if (body?.success && body.data) {
        this.currentStats = body.data
        this.render()
      } else {
        this.renderError()
      }
    } catch {
      this.renderError()
    }
  }

  private getHeaders(): Record<string, string> | null {
    const token = NetworkManager.getInstance().getToken()
    if (!token) return null
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    }
  }

  private render(): void {
    if (!this.currentStats) return
    this.clearContainer()
    this.appendBackground()

    const wrapper = document.createElement('div')
    wrapper.style.cssText = `
      position: relative; z-index: 1;
      max-width: 460px; width: 92%;
      margin: 0 auto; padding: 24px 0 40px;
      display: flex; flex-direction: column; gap: 16px;
    `

    wrapper.appendChild(this.buildTopBar())
    wrapper.appendChild(this.buildTabSwitcher())

    const content = document.createElement('div')
    content.style.cssText = 'display:flex; flex-direction:column; gap:16px;'
    this.tabContentEl = content
    wrapper.appendChild(content)

    this.renderTabContent()

    this.container.appendChild(wrapper)
  }

  private buildTopBar(): HTMLElement {
    const bar = document.createElement('div')
    bar.style.cssText = 'display:flex; align-items:center; justify-content:space-between;'

    const title = document.createElement('h2')
    title.style.cssText = `
      ${neonTitleGlow()}
      font-size: clamp(1.4rem, 5vw, 1.8rem); margin: 0;
    `
    title.textContent = 'Профиль'

    const closeBtn = document.createElement('button')
    closeBtn.type = 'button'
    closeBtn.style.cssText = `
      background: transparent; border: none; color: ${NeonColors.textMuted};
      font-size: 1.6rem; cursor: pointer; padding: 4px 10px;
    `
    closeBtn.textContent = '×'
    closeBtn.addEventListener('click', () => {
      this.onClose?.()
      this.hide()
    })

    bar.appendChild(title)
    bar.appendChild(closeBtn)
    return bar
  }

  private buildTabSwitcher(): HTMLElement {
    const wrap = document.createElement('div')
    wrap.style.cssText = `
      display: flex; gap: 8px;
      background: rgba(0,2,10,0.55);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 12px;
      padding: 4px;
    `

    this.tabButtons.clear()
    const tabs: { id: ProfileTab; label: string }[] = [
      { id: 'stats', label: 'Статистика' },
      { id: 'wallet', label: 'Кошелёк' },
      { id: 'settings', label: 'Настройки' },
    ]
    for (const tab of tabs) {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.style.cssText = `
        flex: 1; padding: 8px 6px; border: none; border-radius: 8px;
        background: transparent; color: ${NeonColors.textMuted};
        font-family: ${FONTS.body}; font-size: 0.88rem; font-weight: 600;
        cursor: pointer; transition: background 0.15s, color 0.15s;
      `
      btn.textContent = tab.label
      btn.addEventListener('click', () => this.selectTab(tab.id))
      wrap.appendChild(btn)
      this.tabButtons.set(tab.id, btn)
    }
    this.applyTabStyles()
    return wrap
  }

  private applyTabStyles(): void {
    for (const [id, btn] of this.tabButtons.entries()) {
      const active = id === this.currentTab
      btn.style.background = active ? 'rgba(251,191,36,0.12)' : 'transparent'
      btn.style.color = active ? NeonColors.primary : NeonColors.textMuted
    }
  }

  private selectTab(tab: ProfileTab): void {
    if (this.currentTab === tab) return
    this.currentTab = tab
    this.applyTabStyles()
    this.renderTabContent()
  }

  private renderTabContent(): void {
    if (!this.tabContentEl || !this.currentStats) return
    while (this.tabContentEl.firstChild) this.tabContentEl.removeChild(this.tabContentEl.firstChild)

    switch (this.currentTab) {
      case 'stats':
        this.renderStatsTab(this.tabContentEl, this.currentStats)
        break
      case 'wallet':
        this.renderWalletTab(this.tabContentEl, this.currentStats)
        break
      case 'settings':
        this.renderSettingsTab(this.tabContentEl)
        break
    }
  }

  // ---------- STATS TAB ----------

  private renderStatsTab(parent: HTMLElement, stats: ProfileSummary): void {
    parent.appendChild(buildHeader(stats))
    parent.appendChild(buildBalances(stats))
    parent.appendChild(buildStatsSection(stats))
    parent.appendChild(buildRecordsSection(stats))
    parent.appendChild(buildStreakSection(stats))
  }

  // ---------- WALLET TAB ----------

  private renderWalletTab(parent: HTMLElement, stats: ProfileSummary): void {
    const network = NetworkManager.getInstance()
    const isTrial = network.freeGamesLeft > 0

    parent.appendChild(buildWalletBalanceBlock(stats, network.freeGamesLeft))

    const actions = document.createElement('div')
    actions.style.cssText = sectionStyle()
    actions.appendChild(sectionLabel('Действия'))

    const buttonsRow = document.createElement('div')
    buttonsRow.style.cssText = 'display:flex; gap:10px; margin-top:12px;'

    const depositBtn = document.createElement('button')
    depositBtn.type = 'button'
    depositBtn.style.cssText = `
      flex: 1; padding: 12px; border-radius: 12px;
      background: ${NeonColors.success}; color: #0f172a;
      border: none; font-weight: 700; font-family: ${FONTS.body};
      cursor: pointer; font-size: 0.95rem;
    `
    depositBtn.textContent = 'Пополнить ⭐'
    depositBtn.addEventListener('click', () => void this.handleDeposit())

    const payoutBtn = document.createElement('button')
    payoutBtn.type = 'button'
    const balance = safeInt(stats.balance)
    const canPayout = balance > 0
    payoutBtn.style.cssText = `
      flex: 1; padding: 12px; border-radius: 12px;
      background: transparent; color: ${NeonColors.gold};
      border: 1px solid ${NeonColors.gold};
      font-weight: 700; font-family: ${FONTS.body};
      cursor: ${canPayout ? 'pointer' : 'not-allowed'}; font-size: 0.95rem;
      opacity: ${canPayout ? '1' : '0.5'};
    `
    payoutBtn.textContent = 'Вывести'
    payoutBtn.disabled = !canPayout
    if (canPayout) payoutBtn.addEventListener('click', () => void this.handlePayout(balance))

    buttonsRow.appendChild(depositBtn)
    buttonsRow.appendChild(payoutBtn)
    actions.appendChild(buttonsRow)

    if (isTrial) {
      const trialEl = document.createElement('div')
      trialEl.style.cssText = `
        margin-top: 12px; padding: 10px 12px; border-radius: 10px;
        background: rgba(0,255,100,0.08); border: 1px solid rgba(0,255,100,0.2);
        color: ${NeonColors.success}; font-size: 0.85rem;
      `
      trialEl.textContent = `Бесплатных игр осталось: ${network.freeGamesLeft}`
      actions.appendChild(trialEl)
    }

    parent.appendChild(actions)

    const hint = document.createElement('div')
    hint.style.cssText = `
      font-size: 0.78rem; color: ${NeonColors.textDim}; text-align:center;
    `
    hint.textContent = '1⭐ = 1 вход • Stars начисляются через Telegram'
    parent.appendChild(hint)
  }

  private async handleDeposit(): Promise<void> {
    const network = NetworkManager.getInstance()
    const token = network.getToken()
    if (!token) return

    const tgWebApp = window.Telegram?.WebApp
    if (!tgWebApp || !('openInvoice' in tgWebApp)) {
      this.showToast('Пополнение доступно только в Telegram', 'error')
      return
    }

    // In-DOM amount picker — window.prompt() is a no-op on Telegram iOS and
    // shows a janky browser dialog elsewhere, so we ship a small modal here.
    const parsed = await this.requestAmount({
      title: 'Сколько Stars пополнить?',
      defaultValue: 1,
      min: 1,
      max: 100,
      submitLabel: 'Пополнить ⭐',
    })
    if (parsed === null) return

    try {
      const paid = await PaymentClient.deposit(parsed, token)
      if (paid) {
        await network.fetchBalance()
        this.showToast(`Пополнено: ${parsed}⭐`, 'success')
        void this.fetchProfile()
      }
    } catch {
      this.showToast('Не удалось пополнить', 'error')
    }
  }

  private async handlePayout(maxAmount: number): Promise<void> {
    const network = NetworkManager.getInstance()
    const token = network.getToken()
    if (!token) return

    const amount = await this.requestAmount({
      title: 'Сколько Stars вывести?',
      defaultValue: maxAmount,
      min: 1,
      max: maxAmount,
      submitLabel: 'Вывести',
    })
    if (amount === null) return

    try {
      await PaymentClient.requestPayout(amount, token)
      this.showToast('Заявка отправлена!', 'success')
      void this.fetchProfile()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка вывода'
      this.showToast(msg, 'error')
    }
  }

  // ---------- SETTINGS TAB ----------

  private renderSettingsTab(parent: HTMLElement): void {
    parent.appendChild(this.buildNameSection())
    parent.appendChild(this.buildSkinSection())
    parent.appendChild(buildNotificationsButton())
  }

  private buildNameSection(): HTMLElement {
    const block = document.createElement('div')
    block.style.cssText = sectionStyle()
    block.appendChild(sectionLabel('Имя в игре'))

    const input = document.createElement('input')
    input.type = 'text'
    input.maxLength = 20
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(NAME_STORAGE_KEY) : null
    const network = NetworkManager.getInstance()
    input.value = (stored ?? network.defaultDisplayName ?? '').trim()
    input.placeholder = 'Например, Cobra'
    input.style.cssText = `
      width: 100%; box-sizing: border-box; margin-top: 10px;
      padding: 12px 14px; border-radius: 12px;
      background: rgba(15,23,42,0.6); border: 1px solid rgba(71,85,105,0.5);
      color: ${NeonColors.textBright}; font-family: ${FONTS.body};
      font-size: 1rem; outline: none; transition: border-color 0.2s;
    `
    input.addEventListener('focus', () => { input.style.borderColor = 'rgba(251,191,36,0.5)' })
    input.addEventListener('blur', () => { input.style.borderColor = 'rgba(71,85,105,0.5)' })
    input.addEventListener('input', () => {
      if (typeof localStorage !== 'undefined') localStorage.setItem(NAME_STORAGE_KEY, input.value)
    })
    block.appendChild(input)

    const hint = document.createElement('div')
    hint.style.cssText = `font-size: 0.74rem; color: ${NeonColors.textDim}; margin-top: 6px;`
    hint.textContent = 'Так тебя будут видеть в матче и в Топе дня'
    block.appendChild(hint)

    return block
  }

  private buildSkinSection(): HTMLElement {
    const block = document.createElement('div')
    block.style.cssText = sectionStyle('center')
    block.appendChild(sectionLabel('Скин'))

    const previewWrap = document.createElement('div')
    previewWrap.style.cssText = `
      display: flex; align-items: center; justify-content: center;
      width: 300px; height: 40px; max-width: 100%;
      margin: 12px auto 6px;
    `
    mountSnakePreview(previewWrap)
    block.appendChild(previewWrap)

    const hint = document.createElement('div')
    hint.style.cssText = `font-size: 0.74rem; color: ${NeonColors.textDim};`
    hint.textContent = 'Тапни по змейке, чтобы сменить цвет'
    block.appendChild(hint)

    return block
  }

  // ---------- shared ----------

  private requestAmount(opts: {
    title: string
    defaultValue: number
    min: number
    max: number
    submitLabel: string
  }): Promise<number | null> {
    return new Promise<number | null>((resolve) => {
      const overlay = document.createElement('div')
      overlay.style.cssText = `
        position: absolute; inset: 0;
        background: rgba(0,2,10,0.75);
        display: flex; align-items: center; justify-content: center;
        z-index: 10010;
        font-family: ${FONTS.body};
      `

      const card = document.createElement('div')
      card.style.cssText = `
        width: 92%; max-width: 320px;
        background: rgba(5,8,22,0.95);
        border: 1px solid rgba(255,255,255,0.12);
        border-radius: 16px;
        padding: 18px;
        display: flex; flex-direction: column; gap: 12px;
      `

      const title = document.createElement('div')
      title.style.cssText = `font-size: 1rem; color: ${NeonColors.textBright}; font-weight: 700;`
      title.textContent = opts.title
      card.appendChild(title)

      const input = document.createElement('input')
      input.type = 'number'
      input.min = String(opts.min)
      input.max = String(opts.max)
      input.value = String(opts.defaultValue)
      input.inputMode = 'numeric'
      input.style.cssText = `
        width: 100%; box-sizing: border-box;
        padding: 12px 14px; border-radius: 12px;
        background: rgba(15,23,42,0.6);
        border: 1px solid rgba(71,85,105,0.5);
        color: ${NeonColors.textBright};
        font-family: ${FONTS.body}; font-size: 1rem;
        outline: none; text-align: center;
      `
      card.appendChild(input)

      const hint = document.createElement('div')
      hint.style.cssText = `font-size: 0.78rem; color: ${NeonColors.textMuted}; text-align:center;`
      hint.textContent = `от ${opts.min} до ${opts.max}`
      card.appendChild(hint)

      const buttons = document.createElement('div')
      buttons.style.cssText = 'display:flex; gap:10px;'

      const cancel = document.createElement('button')
      cancel.type = 'button'
      cancel.style.cssText = `
        flex: 1; padding: 10px; border-radius: 10px;
        background: transparent; color: ${NeonColors.textMuted};
        border: 1px solid rgba(255,255,255,0.12);
        font-family: ${FONTS.body}; cursor: pointer;
      `
      cancel.textContent = 'Отмена'

      const submit = document.createElement('button')
      submit.type = 'button'
      submit.style.cssText = `
        flex: 1; padding: 10px; border-radius: 10px;
        background: ${NeonColors.success}; color: #0f172a;
        border: none; font-weight: 700;
        font-family: ${FONTS.body}; cursor: pointer;
      `
      submit.textContent = opts.submitLabel

      buttons.appendChild(cancel)
      buttons.appendChild(submit)
      card.appendChild(buttons)

      overlay.appendChild(card)
      this.container.appendChild(overlay)

      const cleanup = (): void => {
        overlay.remove()
      }
      cancel.addEventListener('click', () => { cleanup(); resolve(null) })
      submit.addEventListener('click', () => {
        const raw = parseInt(input.value, 10)
        if (!Number.isFinite(raw) || raw < opts.min || raw > opts.max) {
          input.style.borderColor = NeonColors.danger
          return
        }
        cleanup()
        resolve(raw)
      })
      input.focus()
    })
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    const color = type === 'success' ? NeonColors.success : NeonColors.danger
    const toast = document.createElement('div')
    toast.style.cssText = `
      position: absolute; top: 16px; left: 50%; transform: translateX(-50%);
      background: ${color}; color: #fff; padding: 10px 20px;
      border-radius: 12px; font-size: 0.9rem; z-index: 10000;
      box-shadow: 0 0 20px ${color}40;
      transition: opacity 0.3s;
      font-family: ${FONTS.body};
    `
    toast.textContent = message
    mountToAppShell(toast)
    window.setTimeout(() => {
      toast.style.opacity = '0'
      window.setTimeout(() => toast.remove(), 300)
    }, 3000)
  }
}

// ============================================================================
// Pure render helpers
// ============================================================================

function buildHeader(stats: ProfileSummary): HTMLElement {
  const block = document.createElement('div')
  block.style.cssText = sectionStyle('center')

  const name = document.createElement('div')
  name.style.cssText = `font-size: 1.4rem; font-weight: 700; color: ${NeonColors.textBright};`
  name.textContent = resolvePlayerName(stats)
  block.appendChild(name)

  const since = document.createElement('div')
  since.style.cssText = `font-size: 0.78rem; color: ${NeonColors.textMuted}; margin-top: 6px;`
  since.textContent = `С нами с ${formatRegistrationDate(stats.createdAt)}`
  block.appendChild(since)

  return block
}

function buildBalances(stats: ProfileSummary): HTMLElement {
  const row = document.createElement('div')
  row.style.cssText = 'display:flex; gap:10px; flex-wrap:wrap;'

  row.appendChild(buildCompactCard('Баланс', `${safeInt(stats.balance)} ⭐`, NeonColors.primary, { grow: true }))
  row.appendChild(buildCompactCard('Всего вынесено', `${safeInt(stats.totalStarsEarned)} ⭐`, NeonColors.gold, { grow: true }))

  return row
}

function buildWalletBalanceBlock(stats: ProfileSummary, freeGamesLeft: number): HTMLElement {
  const block = document.createElement('div')
  block.style.cssText = sectionStyle('center')

  const labelTop = document.createElement('div')
  labelTop.style.cssText = `font-size: 0.72rem; color: ${NeonColors.textMuted}; text-transform: uppercase; letter-spacing: 0.18em;`
  labelTop.textContent = 'Текущий баланс'
  block.appendChild(labelTop)

  const value = document.createElement('div')
  value.style.cssText = `font-size: 2rem; font-weight: 900; color: ${NeonColors.primary}; margin-top: 6px; display:inline-flex; align-items:center; gap:6px;`
  value.innerHTML = tgStar(28, safeInt(stats.balance))
  block.appendChild(value)

  if (freeGamesLeft > 0) {
    const trial = document.createElement('div')
    trial.style.cssText = `margin-top: 6px; font-size: 0.85rem; color: ${NeonColors.success};`
    trial.textContent = `🎮 + ${freeGamesLeft} бесплатных`
    block.appendChild(trial)
  }

  const lifetime = document.createElement('div')
  lifetime.style.cssText = `margin-top: 10px; font-size: 0.78rem; color: ${NeonColors.textMuted};`
  lifetime.textContent = `Всего вынесено: ${safeInt(stats.totalStarsEarned)}⭐`
  block.appendChild(lifetime)

  return block
}

function buildStatsSection(stats: ProfileSummary): HTMLElement {
  const block = document.createElement('div')
  block.style.cssText = sectionStyle()

  block.appendChild(sectionLabel('Статистика'))

  const grid = document.createElement('div')
  grid.style.cssText = 'display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top:10px;'

  const games = safeInt(stats.gamesPlayed)
  const evacs = safeInt(stats.evacuations)
  const evacRate = formatEvacRate(stats)

  grid.appendChild(buildStatCard('🎮 Игр сыграно', String(games)))
  grid.appendChild(buildStatCard('🚀 Эвакуаций', `${evacs} (${evacRate})`, NeonColors.success))
  grid.appendChild(buildStatCard('💀 Смертей', String(safeInt(stats.deaths)), NeonColors.danger))
  grid.appendChild(buildStatCard('⚔️ Убийств', String(safeInt(stats.kills)), NeonColors.primary))

  block.appendChild(grid)
  return block
}

function buildRecordsSection(stats: ProfileSummary): HTMLElement {
  const block = document.createElement('div')
  block.style.cssText = sectionStyle()

  block.appendChild(sectionLabel('Рекорды'))

  const list = document.createElement('div')
  list.style.cssText = 'display:flex; flex-direction:column; gap:8px; margin-top:10px;'

  list.appendChild(buildRecordRow('Длиннейшая змея', formatNumberOrDash(stats.longestSnake)))
  list.appendChild(buildRecordRow('Лучшая эвакуация', formatStarsOrDash(stats.biggestEvacuation)))
  list.appendChild(buildRecordRow('Лучшее место дня', formatBestDailyRank(stats.bestDailyRank)))
  list.appendChild(buildRecordRow('Рекорд streak', formatStreakOrDash(stats.longestDailyStreak)))

  block.appendChild(list)
  return block
}

function buildStreakSection(stats: ProfileSummary): HTMLElement {
  const block = document.createElement('div')
  block.style.cssText = sectionStyle('center')

  const streak = safeInt(stats.currentDailyStreak)
  const label = document.createElement('div')
  label.style.cssText = `color: ${NeonColors.textMuted}; font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.18em;`
  label.textContent = 'Текущий streak'
  block.appendChild(label)

  const value = document.createElement('div')
  value.style.cssText = `font-size: 1.6rem; font-weight: 700; color: ${NeonColors.gold}; margin-top: 6px;`
  if (streak <= 0) {
    value.textContent = '— Сыграй сегодня, чтобы начать'
    value.style.fontSize = '0.95rem'
    value.style.color = NeonColors.textMuted
  } else {
    value.textContent = `🔥 ${streak} ${pluralDay(streak)}`
  }
  block.appendChild(value)

  return block
}

function buildNotificationsButton(): HTMLElement {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.disabled = true
  btn.style.cssText = `
    margin-top: 4px;
    padding: 12px 16px; border-radius: 12px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.10);
    color: ${NeonColors.textMuted};
    font-family: ${FONTS.body}; font-size: 0.92rem;
    cursor: not-allowed;
    text-align: center;
  `
  btn.textContent = '🔔 Уведомления (скоро)'
  return btn
}

// ---------- small UI primitives ----------

function sectionStyle(align: 'left' | 'center' = 'left'): string {
  return `
    background: rgba(0,2,10,0.55);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 14px;
    padding: 14px 16px;
    text-align: ${align};
  `
}

function sectionLabel(text: string): HTMLElement {
  const el = document.createElement('div')
  el.style.cssText = `
    font-size: 0.72rem; color: ${NeonColors.textMuted};
    text-transform: uppercase; letter-spacing: 0.22em; font-weight: 700;
  `
  el.textContent = text
  return el
}

function buildCompactCard(label: string, value: string, color: string, opts: { grow?: boolean } = {}): HTMLElement {
  const card = document.createElement('div')
  card.style.cssText = `
    ${opts.grow ? 'flex: 1 1 0;' : ''}
    background: rgba(0,2,10,0.55);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 14px;
    padding: 12px 14px;
    text-align: center;
  `

  const lbl = document.createElement('div')
  lbl.style.cssText = `font-size: 0.72rem; color: ${NeonColors.textMuted}; text-transform:uppercase; letter-spacing:0.18em;`
  lbl.textContent = label
  card.appendChild(lbl)

  const val = document.createElement('div')
  val.style.cssText = `font-size: 1.05rem; font-weight: 700; color: ${color}; margin-top: 6px;`
  val.textContent = value
  card.appendChild(val)

  return card
}

function buildStatCard(label: string, value: string, color: string = NeonColors.textBright): HTMLElement {
  const card = document.createElement('div')
  card.style.cssText = `
    background: rgba(255,255,255,0.04);
    border-radius: 10px;
    padding: 10px 12px;
    text-align: center;
  `
  const lbl = document.createElement('div')
  lbl.style.cssText = `font-size: 0.7rem; color: ${NeonColors.textMuted};`
  lbl.textContent = label
  card.appendChild(lbl)

  const val = document.createElement('div')
  val.style.cssText = `font-size: 1rem; font-weight: 700; color: ${color}; margin-top: 4px;`
  val.textContent = value
  card.appendChild(val)

  return card
}

function buildRecordRow(label: string, value: string): HTMLElement {
  const row = document.createElement('div')
  row.style.cssText = `
    display: flex; justify-content: space-between; align-items: center;
    padding: 8px 0; border-bottom: 1px solid rgba(255,255,255,0.06);
    font-size: 0.92rem;
  `

  const lbl = document.createElement('div')
  lbl.style.cssText = `color: ${NeonColors.textMuted};`
  lbl.textContent = label
  row.appendChild(lbl)

  const val = document.createElement('div')
  val.style.cssText = `color: ${NeonColors.textBright}; font-weight: 700;`
  val.textContent = value
  row.appendChild(val)

  return row
}
